import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiArrowUpRight, FiCheck, FiMail, FiMapPin, FiHome } from "react-icons/fi";
import "../css/contactus.scss";

import siteConfig from "../config/siteConfig";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
  privacy: false,
};

const subjectOptions = [
  "Maat & pasvorm",
  "Productinformatie",
  "Bestelling & betaling",
  "Verzending & retour",
  "Herstellingen",
  "Iets anders",
];

const validateForm = (data) => {
  const nextErrors = {};
  const phoneDigits = data.phone.replace(/\D/g, "");

  if (!data.firstName.trim()) nextErrors.firstName = "Vul je voornaam in.";
  if (!data.lastName.trim()) nextErrors.lastName = "Vul je achternaam in.";

  if (!data.email.trim()) {
    nextErrors.email = "Vul je e-mailadres in.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    nextErrors.email = "Vul een geldig e-mailadres in.";
  }

  if (data.phone && (phoneDigits.length < 8 || phoneDigits.length > 15)) {
    nextErrors.phone = "Controleer je telefoonnummer.";
  }
  if (!data.subject) nextErrors.subject = "Kies een onderwerp.";
  if (!data.message.trim()) {
    nextErrors.message = "Vertel ons waarmee we kunnen helpen.";
  } else if (data.message.trim().length < 10) {
    nextErrors.message = "Gebruik minstens 10 tekens.";
  }
  if (!data.privacy) nextErrors.privacy = "Bevestig dat we je bericht mogen verwerken.";

  return nextErrors;
};

function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(siteConfig.contact.address)}`;

  const handleInputChange = (event) => {
    const { name, type, value, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
    setShowSuccess(false);
    if (errors[name]) {
      setErrors((current) => {
        const nextErrors = { ...current };
        delete nextErrors[name];
        return nextErrors;
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formErrors = validateForm(formData);
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      const firstErrorField = Object.keys(formErrors)[0];
      window.requestAnimationFrame(() => document.getElementById(firstErrorField)?.focus());
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    await new Promise((resolve) => window.setTimeout(resolve, 850));
    setFormData(initialFormData);
    setShowSuccess(true);
    setIsSubmitting(false);
  };

  return (
    <main className="contact app-page-enter">
      <section className="contact-hero" aria-labelledby="contact-title">
        <div>
          <p className="eyebrow">Contact / Customer care</p>
          <h1 id="contact-title">Let&apos;s talk<em>trousers.</em></h1>
        </div>
        <div className="contact-hero__intro">
          <span>01 / Ask us anything</span>
          <p>
            Een vraag over pasvorm, stof of je bestelling? Ons team kent de collectie van
            binnen en van buiten en helpt je graag persoonlijk verder.
          </p>
          <a href={`mailto:${siteConfig.contact.email}`} className="text-link">
            Mail direct <FiArrowRight aria-hidden="true" />
          </a>
        </div>
      </section>

      <section className="contact-layout" aria-label="Contact details and message form">
        <aside className="contact-card">
          <div className="contact-card__heading">
            <p className="eyebrow">Hier vind je ons</p>
            <h2>Van mens tot mens.</h2>
            <p>
              Je krijgt antwoord van het team van {siteConfig.displayName}, doorgaans binnen
              één à twee werkdagen.
            </p>
          </div>

          <address className="contact-card__details">
            <a href={`mailto:${siteConfig.contact.email}`}>
              <FiMail aria-hidden="true" />
              <span><small>E-mail</small>{siteConfig.contact.email}</span>
            </a>
            <a href={siteConfig.website}>
              <FiHome aria-hidden="true" />
              <span><small>Bedrijf</small>{siteConfig.companyName}</span>
            </a>
            <a href={mapsHref} target="_blank" rel="noreferrer">
              <FiMapPin aria-hidden="true" />
              <span><small>Bedrijfsadres</small>{siteConfig.contact.address}</span>
            </a>
          </address>

          <div className="contact-card__hours">
            <span>Bereikbaar</span>
            <p>Ma — vr&nbsp;&nbsp;09:00 — 17:00</p>
            <p>Za — zo&nbsp;&nbsp;Gesloten</p>
          </div>

          <figure className="contact-card__visual">
            <img src="/editorial/contact.jpg" alt="Light grey stretch trousers beside fabric swatches" loading="lazy" />
            <figcaption>domtanion / Customer care</figcaption>
          </figure>

          <div className="contact-card__socials" aria-label="Social media">
            {Object.entries(siteConfig.social).map(([name, url]) => (
              <a key={name} href={url} target="_blank" rel="noreferrer">
                {name} <FiArrowUpRight aria-hidden="true" />
              </a>
            ))}
          </div>
        </aside>

        <div className="contact-form-wrap">
          <div className="contact-form-wrap__heading">
            <p className="eyebrow">Stuur een bericht</p>
            <h2>Waarmee kunnen we helpen?</h2>
            <p>Velden met een sterretje zijn verplicht.</p>
          </div>

          {showSuccess && (
            <div className="contact-form__success" role="status" aria-live="polite">
              <FiCheck aria-hidden="true" />
              <div>
                <strong>Je formulier is gecontroleerd.</strong>
                <p>Deze demo verstuurt geen bericht. Mail ons rechtstreeks via <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>.</p>
              </div>
            </div>
          )}

          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className={`contact-field ${errors.firstName ? "has-error" : ""}`}>
              <label htmlFor="firstName">Voornaam *</label>
              <input id="firstName" name="firstName" type="text" autoComplete="given-name" value={formData.firstName} onChange={handleInputChange} aria-invalid={Boolean(errors.firstName)} aria-describedby={errors.firstName ? "firstName-error" : undefined} />
              {errors.firstName && <span id="firstName-error">{errors.firstName}</span>}
            </div>

            <div className={`contact-field ${errors.lastName ? "has-error" : ""}`}>
              <label htmlFor="lastName">Achternaam *</label>
              <input id="lastName" name="lastName" type="text" autoComplete="family-name" value={formData.lastName} onChange={handleInputChange} aria-invalid={Boolean(errors.lastName)} aria-describedby={errors.lastName ? "lastName-error" : undefined} />
              {errors.lastName && <span id="lastName-error">{errors.lastName}</span>}
            </div>

            <div className={`contact-field ${errors.email ? "has-error" : ""}`}>
              <label htmlFor="email">E-mailadres *</label>
              <input id="email" name="email" type="email" autoComplete="email" inputMode="email" value={formData.email} onChange={handleInputChange} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? "email-error" : undefined} />
              {errors.email && <span id="email-error">{errors.email}</span>}
            </div>

            <div className={`contact-field ${errors.phone ? "has-error" : ""}`}>
              <label htmlFor="phone">Telefoon <small>optioneel</small></label>
              <input id="phone" name="phone" type="tel" autoComplete="tel" inputMode="tel" value={formData.phone} onChange={handleInputChange} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? "phone-error" : undefined} />
              {errors.phone && <span id="phone-error">{errors.phone}</span>}
            </div>

            <div className={`contact-field contact-field--full ${errors.subject ? "has-error" : ""}`}>
              <label htmlFor="subject">Onderwerp *</label>
              <select id="subject" name="subject" value={formData.subject} onChange={handleInputChange} aria-invalid={Boolean(errors.subject)} aria-describedby={errors.subject ? "subject-error" : undefined}>
                <option value="">Selecteer een onderwerp</option>
                {subjectOptions.map((subject) => <option key={subject} value={subject}>{subject}</option>)}
              </select>
              {errors.subject && <span id="subject-error">{errors.subject}</span>}
            </div>

            <div className={`contact-field contact-field--full ${errors.message ? "has-error" : ""}`}>
              <div className="contact-field__label-row">
                <label htmlFor="message">Je bericht *</label>
                <small>{formData.message.length} / 1000</small>
              </div>
              <textarea id="message" name="message" rows="7" maxLength="1000" value={formData.message} onChange={handleInputChange} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? "message-error" : undefined} />
              {errors.message && <span id="message-error">{errors.message}</span>}
            </div>

            <div className={`contact-consent contact-field--full ${errors.privacy ? "has-error" : ""}`}>
              <input id="privacy" name="privacy" type="checkbox" checked={formData.privacy} onChange={handleInputChange} aria-invalid={Boolean(errors.privacy)} aria-describedby={errors.privacy ? "privacy-error" : undefined} />
              <label htmlFor="privacy">
                Ik ga ermee akkoord dat mijn gegevens worden gebruikt om mijn vraag te
                beantwoorden. Lees ons <Link to="/privacy">privacybeleid</Link>. *
              </label>
              {errors.privacy && <span id="privacy-error">{errors.privacy}</span>}
            </div>

            <div className="contact-form__footer contact-field--full">
              <button className="solid-button" type="submit" disabled={isSubmitting}>
                <span>{isSubmitting ? "Even geduld…" : "Verstuur bericht"}</span>
                {!isSubmitting && <FiArrowRight aria-hidden="true" />}
              </button>
              <p>Persoonlijk antwoord binnen één à twee werkdagen.</p>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}

export default Contact;
